/** Phase 4.1.0 — Autopilot action queue (cross-run).
 *
 * Filterable table. Inline approve/reject (manager+) / rollback (owner+admin only).
 * Empty state with link back to /app/autopilot.
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import AutopilotEvidenceChips from "../../components/app/AutopilotEvidenceChips";
import { useLocale } from "../../lib/i18n";

const STATES = ["pending", "queued_for_approval", "executing", "executed",
                 "rejected", "failed", "rolled_back"];
const ACTION_TYPES = ["pause_campaign", "resume_campaign", "generate_optimization_report",
                       "budget_increase", "budget_decrease", "duplicate_winning_campaign",
                       "generate_new_creative_variant", "request_human_approval"];

const STATE_TONE = {
  pending: "secondary", queued_for_approval: "secondary",
  executing: "default", executed: "default",
  rejected: "destructive", failed: "destructive",
  rolled_back: "outline",
};

function canApprove(role) { return ["owner", "admin", "manager"].includes(role); }
function canRollback(role) { return ["owner", "admin"].includes(role); }

function StateBadge({ state }) {
  return <Badge variant={STATE_TONE[state] || "outline"} data-testid={`action-state-${state}`}>{state}</Badge>;
}

export default function AutopilotActions() {
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const me = auth.getUser() || { role: "" };
  const approver = canApprove(me.role);
  const roller = canRollback(me.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("all");
  const [actionType, setActionType] = useState("all");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // { action, kind: "approve"|"reject"|"rollback" }
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    const params = { limit: 50 };
    if (state !== "all") params.state = state;
    if (actionType !== "all") params.action_type = actionType;
    try {
      const r = await api.get("/autopilot/actions", { params, skipPermissionToast: true });
      setItems(r.data.items || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [state, actionType]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const lc = q.toLowerCase();
    return items.filter((a) =>
      (a.action_type || "").toLowerCase().includes(lc) ||
      (a.rationale || "").toLowerCase().includes(lc) ||
      (a.target_resource_id || "").toLowerCase().includes(lc));
  }, [items, q]);

  async function act() {
    if (!dialog) return;
    if (dialog.kind === "reject" && !comment.trim()) {
      toast.error(t("autopilot.actions.comment_required"));
      return;
    }
    setBusy(true);
    try {
      const url = dialog.kind === "escalate"
        ? `/autopilot/actions/${dialog.action.id}/escalate-to-approval`
        : `/autopilot/actions/${dialog.action.id}/${dialog.kind}`;
      await api.post(url, {
        comment: comment || undefined, reason: comment || undefined,
      });
      toast.success(t(`autopilot.actions.${dialog.kind}_success`));
      setDialog(null); setComment("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || t("autopilot.actions.action_failed")));
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="autopilot-actions-page" className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("autopilot.actions.page_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("autopilot.actions.subtitle")}</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              data-testid="autopilot-actions-search"
              placeholder={t("autopilot.actions.search_placeholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Select value={state} onValueChange={setState}>
              <SelectTrigger data-testid="autopilot-actions-state-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="state-filter-all">{t("autopilot.actions.all_states")}</SelectItem>
                {STATES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`state-filter-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger data-testid="autopilot-actions-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="type-filter-all">{t("autopilot.actions.all_types")}</SelectItem>
                {ACTION_TYPES.map((at) => (
                  <SelectItem key={at} value={at} data-testid={`type-filter-${at}`}>
                    {t(`autopilot.action_types.${at}`) || at}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} data-testid="autopilot-actions-refresh">
              {t("autopilot.actions.refresh")}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("autopilot.actions.col_created")}</TableHead>
                <TableHead>{t("autopilot.actions.col_type")}</TableHead>
                <TableHead>{t("autopilot.actions.col_target")}</TableHead>
                <TableHead>{t("autopilot.actions.col_state")}</TableHead>
                <TableHead>{t("autopilot.actions.col_confidence")}</TableHead>
                <TableHead>{t("autopilot.actions.col_rationale")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">{t("common.loading")}</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8"
                    data-testid="autopilot-actions-empty">
                    {t("autopilot.actions.empty")}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((a) => (
                <Fragment key={a.id}>
                <TableRow data-testid={`action-row-${a.id}`}>
                  <TableCell className="text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]"
                    data-testid={`action-row-type-${a.id}`}>
                    {t(`autopilot.action_types.${a.action_type}`) || a.action_type}
                  </Badge></TableCell>
                  <TableCell className="text-xs font-mono">
                    {a.target_resource_id
                      ? `${a.target_resource_type}/${(a.target_resource_id || "").slice(0, 8)}`
                      : "—"}
                  </TableCell>
                  <TableCell><StateBadge state={a.state} /></TableCell>
                  <TableCell className="text-xs font-mono">{(a.confidence || 0).toFixed(2)}</TableCell>
                  <TableCell className="max-w-[280px] truncate text-xs" title={a.rationale}>
                    {a.rationale || "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {a.run_id && (
                      <Button size="sm" variant="ghost"
                        data-testid={`action-open-run-${a.id}`}
                        onClick={() => nav(`/app/autopilot/runs/${a.run_id}`)}>
                        {t("autopilot.actions.open_run")}
                      </Button>
                    )}
                    {a.state === "pending" && approver && (
                      <Button size="sm" variant="outline"
                        data-testid={`action-escalate-btn-${a.id}`}
                        onClick={() => { setDialog({ action: a, kind: "escalate" }); setComment(""); }}>
                        {t("autopilot.actions.escalate")}
                      </Button>
                    )}
                    {a.state === "queued_for_approval" && approver && (
                      <>
                        <Button size="sm" variant="default"
                          data-testid={`action-approve-btn-${a.id}`}
                          onClick={() => { setDialog({ action: a, kind: "approve" }); setComment(""); }}>
                          {t("autopilot.actions.approve")}
                        </Button>
                        <Button size="sm" variant="destructive"
                          data-testid={`action-reject-btn-${a.id}`}
                          onClick={() => { setDialog({ action: a, kind: "reject" }); setComment(""); }}>
                          {t("autopilot.actions.reject")}
                        </Button>
                      </>
                    )}
                    {a.state === "executed" && roller && !a.rolled_back_at && (
                      <Button size="sm" variant="outline"
                        data-testid={`action-rollback-btn-${a.id}`}
                        onClick={() => { setDialog({ action: a, kind: "rollback" }); setComment(""); }}>
                        {t("autopilot.actions.rollback")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {(a.evidence_memory_entry_ids || []).length > 0 && (
                  <TableRow data-testid={`action-row-evidence-${a.id}`}>
                    <TableCell colSpan={7} className="pt-0 pb-2 pl-4">
                      <AutopilotEvidenceChips
                        actionId={a.id}
                        locale={locale}
                        onOpenEntry={(eid) =>
                          nav(`/app/strategy-memory?entry_id=${eid}`)}
                      />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="action-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialog && t(`autopilot.actions.${dialog.kind}_title`)}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-muted-foreground" data-testid="action-dialog-summary">
                <Badge variant="outline" className="font-mono text-[10px] mr-1">
                  {t(`autopilot.action_types.${dialog.action.action_type}`) || dialog.action.action_type}
                </Badge>
                {dialog.action.target_resource_id
                  ? `${dialog.action.target_resource_type}/${dialog.action.target_resource_id.slice(0, 12)}`
                  : ""}
              </div>
              {dialog.action.rationale && (
                <p className="text-xs italic text-muted-foreground">{dialog.action.rationale}</p>
              )}
              <Textarea
                data-testid="action-dialog-comment"
                placeholder={dialog.kind === "reject"
                  ? t("autopilot.actions.reject_placeholder")
                  : t("autopilot.actions.comment_placeholder")}
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              {dialog.kind === "rollback" && (
                <p className="text-xs text-amber-700 dark:text-amber-300"
                  data-testid="action-dialog-rollback-warning">
                  {t("autopilot.actions.rollback_warning")}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" data-testid="action-dialog-cancel">{t("common.cancel")}</Button>
            </DialogClose>
            <Button
              disabled={busy || (dialog?.kind === "reject" && !comment.trim())}
              variant={dialog?.kind === "reject" ? "destructive" : "default"}
              onClick={act}
              data-testid="action-dialog-confirm">
              {busy ? t("autopilot.actions.working") : t(`autopilot.actions.${dialog?.kind || "approve"}_confirm`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
